import CustomChip from '@app/components/atoms/CustomChip/CustomChip'
import Section from '@app/components/atoms/Section/Section'
import CardWidget from '@app/components/CardWidget'
import MenuItem from '@app/components/MenuItem'
import ModalUpdateStatusCampaign from '@app/components/ModalUpdateStatusCampaign'
import OptionMenu from '@app/components/OptionMenu'
import getCDNImage from '@app/libs/getCDNImage'
import theme from '@app/libs/theme'
import useRemember from '@app/libs/useRemember'
import { Inertia } from '@inertiajs/inertia'
import { InertiaLink } from '@inertiajs/inertia-react'
import { ExpandMore } from '@mui/icons-material'
import EditIcon from '@mui/icons-material/Edit'
import LockIcon from '@mui/icons-material/Lock'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import Chip from '@mui/material/Chip'
import Collapse from '@mui/material/Collapse'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

const IconButtonCustomizer = styled(IconButton)((props) => {
  if (props['aria-expanded']) {
    return {
      transform: 'rotate(180deg)',
      marginLeft: 'auto',
      transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shortest,
      }),
    }
  }
  return {
    transform: 'rotate(0deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  }
})

const MerchantDetail = ({ merchant }) => {
  const { t } = useTranslation()

  const [allExpandedMerchant, setAllExpandedMerchant] = useRemember(`MerchantDetail:${merchant.id}`, {
    description: true,
    logo: true,
  })

  const handleExpand = (attribute) => {
    setAllExpandedMerchant({ ...allExpandedMerchant, [attribute]: !allExpandedMerchant[attribute] })
  }
  const [anchorEl, setAnchorEl] = useState(null)
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const [openModalConfirm, setOpenModalConfirm] = useState(false)
  const [modalName, setModalName] = useState('')
  const handleCloseModal = () => setOpenModalConfirm(false)

  const renderModal = (modalName) => {
    switch (modalName) {
      case 'ActiveOrDeactive':
        return (
          <ModalUpdateStatusCampaign
            content={
              'active' === merchant.status ? t('MODAL.DEACTIVE_CONTENT_MERCHANT') : t('MODAL.ACTIVE_CONTENT_MERCHANT')
            }
            handleCloseModal={handleCloseModal}
            handleSubmit={() =>
              Inertia.get(route('admin.merchants.status.change', { merchant: merchant.id, status: merchant.status }))
            }
            openModalConfirm={openModalConfirm}
            title={'active' === merchant.status ? t('MODAL.DEACTIVE_MERCHANT') : t('MODAL.ACTIVE_MERCHANT')}
          />
        )
      default:
        return null
    }
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Section>
          <Grid container spacing={2}>
            <Grid item xs={11}>
              <Typography variant="h5">{merchant.name}</Typography>
            </Grid>
            <Grid item xs={1}>
              <OptionMenu
                anchorEl={anchorEl}
                handleClick={handleClick}
                handleClose={handleClose}
                open={null !== anchorEl}
              >
                <MenuItem
                  component={InertiaLink}
                  href={route('admin.merchants.edit', merchant.id)}
                  icon={EditIcon}
                  name={t('ADMIN_MERCHANT_SHOW.EDIT')}
                />
                <MenuItem
                  icon={'active' === merchant.status ? LockIcon : LockOpenIcon}
                  name={
                    'active' === merchant.status ? t('ADMIN_MERCHANT_SHOW.INACTIVE') : t('ADMIN_MERCHANT_SHOW.ACTIVE')
                  }
                  onClick={() => {
                    handleClose()
                    setModalName('ActiveOrDeactive')
                    setOpenModalConfirm(true)
                  }}
                />
              </OptionMenu>
            </Grid>
            {renderModal(modalName)}
            <Grid item xs={8}>
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <Typography color="textSecondary" variant="body1">
                    {t('ADMIN_MERCHANT_SHOW.EMAIL')}
                  </Typography>
                </Grid>
                <Grid item xs={9}>
                  <Typography variant="body1">{merchant.email}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography color="textSecondary" variant="body1">
                    {t('ADMIN_MERCHANT_SHOW.PHONE_NUMBER')}
                  </Typography>
                </Grid>
                <Grid item xs={9}>
                  <Typography variant="body1">{merchant.phone}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography color="textSecondary" variant="body1">
                    {t('ADMIN_MERCHANT_SHOW.ADDRESS')}
                  </Typography>
                </Grid>
                <Grid item xs={9}>
                  <Typography variant="body1">{merchant.address}</Typography>
                </Grid>

                <Grid item xs={3}>
                  <Typography color="textSecondary" variant="body1">
                    {t('ADMIN_MERCHANT_SHOW.CATEGORY')}
                  </Typography>
                </Grid>

                <Grid item xs={9}>
                  {merchant.categories.map((item) => (
                    <Chip key={item.id} label={item.name} style={{ margin: '2px' }} />
                  ))}
                </Grid>

                <Grid item xs={3}>
                  <Typography color="textSecondary" variant="body1">
                    {t('ADMIN_MERCHANT_SHOW.STATUS')}
                  </Typography>
                </Grid>
                <Grid item xs={9}>
                  <CustomChip
                    color={merchant.status}
                    label={t(`ADMIN_MERCHANT_SHOW.${merchant.status.toUpperCase()}`)}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Section>
      </Grid>

      <Grid item xs={12}>
        <Section>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h5">
                {t('MERCHANT.LOGO')}{' '}
                <IconButtonCustomizer
                  aria-expanded={allExpandedMerchant.logo}
                  aria-label="show more"
                  onClick={() => handleExpand('logo')}
                >
                  <ExpandMore />
                </IconButtonCustomizer>
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Collapse unmountOnExit in={allExpandedMerchant.logo} timeout="auto">
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <Typography variant="body2">{t('MERCHANT.LOGO')}</Typography>
                  </Grid>
                  <img src={getCDNImage(merchant.logo?.link, '300:300')} width={300} />
                </Grid>
              </Collapse>
            </Grid>
          </Grid>
        </Section>
      </Grid>

      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <CardWidget
              button={t('ADMIN_MERCHANT_SHOW.VIEW_ALL')}
              href={route('admin.vouchers.index', { merchant_id: merchant.id })}
              primary={merchant.vouchers_count}
              title={t('ADMIN_MERCHANT_SHOW.TOTAL_VOUCHERS')}
            />
          </Grid>
          <Grid item xs={6}>
            <CardWidget
              button={t('ADMIN_MERCHANT_SHOW.VIEW_ALL')}
              href={route('admin.pos.index', { merchant_id: merchant.id })}
              primary={merchant.pos_count}
              title={t('ADMIN_MERCHANT_SHOW.TOTAL_POS')}
            />
          </Grid>
          <Grid item xs={3}>
            <CardWidget
              color="warning"
              primary={merchant.codes_count}
              primaryUnit={t('ADMIN_MERCHANT_SHOW.CODES')}
              title={t('ADMIN_MERCHANT_SHOW.TOTAL_CODES')}
            />
          </Grid>
          <Grid item xs={3}>
            <CardWidget
              color="success"
              primary={merchant.redeemed_codes_count}
              primaryUnit={t('ADMIN_MERCHANT_SHOW.CODES')}
              title={t('ADMIN_MERCHANT_SHOW.REDEEMED_CODES')}
            />
          </Grid>
          <Grid item xs={3}>
            <CardWidget
              color="danger"
              primary={merchant.used_codes_count}
              primaryUnit={t('ADMIN_MERCHANT_SHOW.CODES')}
              title={t('ADMIN_MERCHANT_SHOW.USED_CODES')}
            />
          </Grid>
          <Grid item xs={3}>
            <CardWidget
              color="info"
              primary={merchant.available_codes_count}
              primaryUnit={t('ADMIN_MERCHANT_SHOW.CODES')}
              title={t('ADMIN_MERCHANT_SHOW.AVAILABLE_CODES')}
            />
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Section>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h5">
                {t('ADMIN_MERCHANT_SHOW.DESCRIPTION')}{' '}
                <IconButtonCustomizer
                  aria-expanded={allExpandedMerchant.description}
                  aria-label="show more"
                  onClick={() => handleExpand('description')}
                >
                  <ExpandMore />
                </IconButtonCustomizer>
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Collapse unmountOnExit in={allExpandedMerchant.description} timeout="auto">
                {merchant.description ? (
                  <Typography dangerouslySetInnerHTML={{ __html: merchant.description }} variant="body1"></Typography>
                ) : (
                  <Typography variant="body1">{t('DATA.NO_DATA')}</Typography>
                )}
              </Collapse>
            </Grid>
          </Grid>
        </Section>
      </Grid>
    </Grid>
  )
}

export default MerchantDetail
